import { useState, useCallback, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { map } from 'lodash';
import * as moment from 'moment';
import classNames from 'classnames';
import Images from 'assets/Images';
import { textFormatter } from 'utils';
import './style.scss';

export default function Table(props) {
	const { tableHeader, tableData, handleSort, scrollEvent, handleScroll } = props;
	const [label, setLabel] = useState();
	const [arrow, setArrow] = useState();
	const [downloadToggler, setDownloadToggler] = useState([]);
	const observer = useRef();
	const downloadRef = useRef();
	const dropdownOptionsRef = useRef();

	const setDateFormat = date => {
		return date ? moment(date).format('DD-MM-YYYY HH:MM') : '-';
	};

	const lastElementRef = useCallback(
		node => {
			observer?.current && observer?.current?.disconnect();
			observer.current = new IntersectionObserver(entries => {
				if (entries[0].isIntersecting && scrollEvent) {
					handleScroll(20);
				}
			});
			node && observer?.current?.observe(node);
		},
		[scrollEvent]
	);

	// outside click handle function
	const handleClickOutside = useCallback(
		event => {
			if (downloadRef?.current && !dropdownOptionsRef?.current?.contains(event?.target) && !downloadRef?.current?.contains(event?.target)) {
				setDownloadToggler(null);
			}
		},
		[downloadToggler]
	);

	// outside click handler
	useEffect(() => {
		document.addEventListener('click', handleClickOutside, true);
		return () => {
			document.removeEventListener('click', handleClickOutside, true);
		};
	}, [downloadToggler, handleClickOutside]);

	// status stat display in table
	const accountStatsDisplay = (data, i) => {
		return (
			<ul className='table-container__stats'>
				<li>
					<img src={Images.noun_sum} alt='noun' /> {data?.ufl_totalAccounts || 0}
				</li>
				<li>
					<img src={Images.success} alt='success' /> {data?.ufl_successAccounts || 0}
				</li>
				<li>
					<img src={Images.failed} alt='failed' /> {data?.ufl_failedAccounts || 0}
				</li>
				<li className='table-container__accounts'>
					<img ref={downloadRef} src={Images.download} alt='download' onClick={() => setDownloadToggler(i)} />
				</li>
				{downloadToggler === i && (
					<div ref={dropdownOptionsRef} className='table-container__download'>
						{data?.ufl_successAccounts > 0 && <p onClick={() => props.handleFile('Actual', data?.ufl_uploadBatchId)}>Actual File</p>}
						{data?.ufl_failedAccounts > 0 && <p onClick={() => props.handleFile('Error', data?.ufl_uploadBatchId)}>Error File</p>}
					</div>
				)}
			</ul>
		);
	};

	const tableBody = tableData?.map((data, i) => (
		<tr ref={lastElementRef} key={i}>
			{tableHeader?.map((header, j) => (
				<td key={j}>
					{header?.field_key === 'ufl_status' ? (
						<span
							className={classNames({
								'table-container--status': true,
								'table-container--success': data[header?.field_key] === 'COMPLETED',
								'table-container--failed': data[header?.field_key] === 'FAILED',
								'table-container--inprogress': ['INPROGRESS', 'PARTIAL'].includes(data[header?.field_key])
							})}
						>
							{textFormatter(data[header?.field_key])}
						</span>
					) : header.field_key === 'updatedBy' ? (
						<>
							{data?.updatedBy || '-'}
							<span className='table-container__user'>{setDateFormat(data?.updatedAt)}</span>
						</>
					) : header?.field_key === 'ufl_totalAccounts' ? (
						accountStatsDisplay(data, i)
					) : (
						header?.field_key === 'c_currencyCode' ? data[header?.field_key].toUpperCase() : textFormatter(data[header?.field_key]) || '-'
					)}
				</td>
			))}
		</tr>
	));

	// handle sort function in table headers
	const handleSortMethod = (el, arrow, value) => {
		setLabel(el?.label);
		setArrow(arrow);
		handleSort({ id: el?.field_key, value });
	};

	// table headers
	const tableHead = map(tableHeader, (el, i) => (
		<th key={i}>
			{el.label}
			<div className='sort-arrow'>
				<span onClick={() => handleSortMethod(el, 'up', 1)} className={classNames({ 'sort-arrow__up': true, 'active-up': label === el?.label && arrow === 'up' })}></span>
				<span onClick={() => handleSortMethod(el, 'down', -1)} className={classNames({ 'sort-arrow__down': true, 'active-down': label === el?.label && arrow === 'down' })}></span>
			</div>
		</th>
	));

	return (
		<>
			<div className='table-container scroll-bar'>
				<table>
					<thead>
						<tr>{tableHead}</tr>
					</thead>
					<tbody>{tableBody}</tbody>
				</table>
			</div>
		</>
	);
}

Table.propTypes = {
	header: PropTypes.array,
	data: PropTypes.array,
	height: PropTypes.number
};

Table.defaultProps = {
	header: [],
	data: [],
	height: 200
};
